import { DataTable, usePagination } from '@/components/table/DataTable';
import {
  DateRangeSelect,
  type DateRange,
} from '@/components/ui/date-range-select';
import {
  FilterOptionList,
  type FilterOption,
} from '@/components/ui/filter-option-list';
import { FilterPopoverTrigger } from '@/components/ui/filter-popover-trigger';
import { FlexCol, FlexRow } from '@/components/ui/flex';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import {
  useConsultantTriageService,
  useConsultantTriageUpdatesService,
  useConsultantUpdatesService,
} from '@/services/consultant';
import { cn } from '@/lib/utils';
import type { ConsultantTriageUpdate } from '@ditto/shared';
import {
  getPaginationRowModel,
  getSortedRowModel,
  type ColumnDef,
} from '@tanstack/react-table';
import dayjs from 'dayjs';
import { useCallback, useMemo, useState, type FC } from 'react';
import { toast } from 'sonner';
import { useConsultantContext } from './ConsultantLayout';
import { EditSummaryModal } from './components/EditSummaryModal';
import { TriageCell } from './components/TriageCell';

const TYPE_OPTIONS: FilterOption[] = [
  { value: 'news', label: 'News' },
  { value: 'guidance', label: 'Guidance' },
  { value: 'regulation', label: 'Regulation' },
  { value: 'standard', label: 'Standard' },
];

const TRIAGE_STATUS_OPTIONS: FilterOption[] = [
  { value: 'all_triaged', label: 'All triaged' },
  { value: 'partial', label: 'Partially triaged' },
  { value: 'none', label: 'Not triaged' },
];

const OptionFilter: FC<{
  label: string;
  options: FilterOption[];
  selected: string[];
  onChange: (selected: string[]) => void;
}> = ({ label, options, selected, onChange }) => {
  const isActive = selected.length > 0;
  const activeLabel =
    selected.length === 1
      ? options.find(o => o.value === selected[0])?.label
      : `${selected.length} selected`;

  return (
    <FilterPopoverTrigger
      label={label}
      isActive={isActive}
      operator={
        isActive ? (selected.length === 1 ? 'is' : 'is any of') : undefined
      }
      activeContent={isActive ? activeLabel : undefined}
      onClear={isActive ? () => onChange([]) : undefined}
    >
      <FilterOptionList
        options={options}
        selected={selected}
        onChange={onChange}
      />
    </FilterPopoverTrigger>
  );
};

export const ConsultantTriagePage: FC = () => {
  const { orgId } = useConsultantContext();
  const [dateRange, setDateRange] = useState<DateRange>({
    from: undefined,
    to: undefined,
  });
  const [selectedSources, setSelectedSources] = useState<string[]>([]);
  const [selectedTypes, setSelectedTypes] = useState<string[]>([]);
  const [selectedTriageStatus, setSelectedTriageStatus] = useState<string[]>(
    []
  );

  const query = useMemo(
    () => ({
      ...(dateRange.from ? { fromDate: dateRange.from } : {}),
      ...(dateRange.to ? { toDate: dateRange.to } : {}),
      ...(selectedSources.length > 0
        ? { source: selectedSources.join(',') }
        : {}),
      ...(selectedTypes.includes('news') && selectedTypes.length === 1
        ? { kind: 'news' }
        : {}),
      ...(selectedTypes.filter(t => t !== 'news').length > 0
        ? { documentType: selectedTypes.filter(t => t !== 'news').join(',') }
        : {}),
    }),
    [dateRange, selectedSources, selectedTypes]
  );

  const { updates, isLoading, isFetching } = useConsultantTriageUpdatesService(
    orgId,
    query
  );
  const { updates: fullUpdates } = useConsultantUpdatesService(orgId, query);
  const { triage } = useConsultantTriageService();

  const [editUpdateId, setEditUpdateId] = useState<string | null>(null);
  const editItem = useMemo(
    () => fullUpdates.find(u => u.id === editUpdateId) ?? null,
    [fullUpdates, editUpdateId]
  );

  // Optimistic state for pending triage changes
  const [optimistic, setOptimistic] = useState<
    Map<string, 'applicable' | 'not_applicable'>
  >(new Map());

  const getTriageStatus = useCallback(
    (update: ConsultantTriageUpdate) => {
      const subscribedWorkspaces = update.workspaceTriage.filter(
        w => w.subscribed
      );
      if (subscribedWorkspaces.length === 0) return 'none';

      const triaged = subscribedWorkspaces.filter(w => {
        const optKey = `${update.id}:${w.workspaceId}`;
        const effectiveStatus = optimistic.get(optKey) ?? w.applicabilityStatus;
        return (
          effectiveStatus === 'applicable' ||
          effectiveStatus === 'not_applicable' ||
          effectiveStatus === 'out_of_scope'
        );
      });

      if (triaged.length === subscribedWorkspaces.length) return 'all_triaged';
      if (triaged.length > 0) return 'partial';
      return 'none';
    },
    [optimistic]
  );

  // Client-side triage status filter
  const filteredUpdates = useMemo(() => {
    if (selectedTriageStatus.length === 0) return updates;
    return updates.filter(u =>
      selectedTriageStatus.includes(getTriageStatus(u))
    );
  }, [updates, selectedTriageStatus, getTriageStatus]);

  // Extract workspace columns from first update (all updates share the same workspaces)
  const workspaces = useMemo(() => {
    if (updates.length === 0) return [];
    return updates[0].workspaceTriage
      .map(w => ({
        id: w.workspaceId,
        name: w.workspaceName,
      }))
      .sort((a, b) => a.name.localeCompare(b.name));
  }, [updates]);

  const sourceOptions: FilterOption[] = useMemo(() => {
    const seen = new Map<string, string>();
    for (const u of updates) {
      if (u.source) seen.set(u.source.id, u.source.title);
    }
    return [...seen.entries()]
      .map(([id, title]) => ({ value: id, label: title }))
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [updates]);

  const handleTriage = useCallback(
    (
      updateId: string,
      workspaceId: string,
      status: 'applicable' | 'not_applicable'
    ) => {
      const optKey = `${updateId}:${workspaceId}`;
      setOptimistic(prev => new Map(prev).set(optKey, status));

      triage(
        { orgId, updateId, workspaceId, status },
        {
          onError: () => {
            toast.error('Failed to save triage');
            setOptimistic(prev => {
              const next = new Map(prev);
              next.delete(optKey);
              return next;
            });
          },
        }
      );
    },
    [orgId, triage]
  );

  const columns: ColumnDef<ConsultantTriageUpdate>[] = useMemo(() => {
    const base: ColumnDef<ConsultantTriageUpdate>[] = [
      {
        id: 'date',
        header: 'Date',
        size: 100,
        minSize: 100,
        maxSize: 100,
        cell: ({ row }) => (
          <span className="text-xs text-neutral-500 whitespace-nowrap">
            {dayjs(row.original.publicationDate).format('DD/MM/YYYY')}
          </span>
        ),
      },
      {
        id: 'title',
        header: 'Title',
        meta: {
          headerClassName: 'min-w-[250px] max-w-[250px]',
          cellClassName: 'min-w-[250px] max-w-[250px]',
        },
        cell: ({ row }) => {
          const title = row.original.title ?? '-';
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block truncate text-sm">{title}</span>
              </TooltipTrigger>
              <TooltipContent side="bottom" className="max-w-[400px]">
                {title}
              </TooltipContent>
            </Tooltip>
          );
        },
      },
      {
        id: 'source',
        header: 'Source',
        meta: {
          headerClassName: 'min-w-[150px] max-w-[150px]',
          cellClassName: 'min-w-[150px] max-w-[150px]',
        },

        cell: ({ row }) => {
          const title = row.original.source?.title ?? '-';
          return (
            <Tooltip>
              <TooltipTrigger asChild>
                <span className="block truncate text-xs text-neutral-600">
                  {title}
                </span>
              </TooltipTrigger>
              <TooltipContent>{title}</TooltipContent>
            </Tooltip>
          );
        },
      },
    ];

    const wsColumns: ColumnDef<ConsultantTriageUpdate>[] = workspaces.map(
      ws => ({
        id: `ws-${ws.id}`,
        header: () => (
          <Tooltip>
            <TooltipTrigger asChild>
              <span className="block truncate text-xs text-center">
                {ws.name}
              </span>
            </TooltipTrigger>
            <TooltipContent>{ws.name}</TooltipContent>
          </Tooltip>
        ),
        size: 100,
        minSize: 100,
        maxSize: 100,
        cell: ({ row }) => {
          const wsTriage = row.original.workspaceTriage.find(
            w => w.workspaceId === ws.id
          );
          if (!wsTriage) return null;
          const optKey = `${row.original.id}:${ws.id}`;
          const effectiveStatus =
            optimistic.get(optKey) ?? wsTriage.applicabilityStatus;
          return (
            <TriageCell
              subscribed={wsTriage.subscribed}
              applicabilityStatus={effectiveStatus}
              onToggle={status => handleTriage(row.original.id, ws.id, status)}
            />
          );
        },
      })
    );

    return [...base, ...wsColumns];
  }, [workspaces, optimistic, handleTriage]);

  const { pagination } = usePagination({
    initialSize: 50,
    manualPagination: false,
  });

  return (
    <FlexCol gap="6" w="full" p="6" h="full" minW="0">
      <div>
        <h2 className="text-xl font-semibold">Client Triage</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Mark regulatory updates as applicable or not applicable for each
          client
        </p>
      </div>

      <FlexRow gap="2" alignItems="center" className="flex-wrap">
        <DateRangeSelect
          value={dateRange}
          onChange={setDateRange}
          label="Publication date"
          placeholder="Publication date"
          calendarOnly
        />
        {sourceOptions.length > 0 && (
          <OptionFilter
            label="Source"
            options={sourceOptions}
            selected={selectedSources}
            onChange={setSelectedSources}
          />
        )}
        <OptionFilter
          label="Type"
          options={TYPE_OPTIONS}
          selected={selectedTypes}
          onChange={setSelectedTypes}
        />
        <OptionFilter
          label="Triage status"
          options={TRIAGE_STATUS_OPTIONS}
          selected={selectedTriageStatus}
          onChange={setSelectedTriageStatus}
        />
        <span className="text-xs text-muted-foreground ml-auto">
          {filteredUpdates.length} update(s)
        </span>
      </FlexRow>

      <div
        className={cn(
          'flex-1 min-h-0 min-w-0',
          isFetching &&
            !isLoading &&
            'opacity-50 transition-opacity duration-200'
        )}
      >
        <DataTable
          columns={columns}
          data={filteredUpdates}
          isLoading={isLoading}
          pagination={pagination}
          rowCount={filteredUpdates.length}
          scrollX
          stickyScrollbar
          grid="bordered"
          className="h-full"
          onRowClick={u => setEditUpdateId(u.id)}
          options={{
            getPaginationRowModel: getPaginationRowModel(),
            getSortedRowModel: getSortedRowModel(),
            autoResetPageIndex: false,
            initialState: {
              columnPinning: {
                left: ['date', 'title', 'source'],
                right: [],
              },
            },
          }}
        />
      </div>

      {editItem && (
        <EditSummaryModal
          open={!!editItem}
          onOpenChange={open => !open && setEditUpdateId(null)}
          update={editItem}
        />
      )}
    </FlexCol>
  );
};
